// frontend/src/components/ScheduleApplyModal.js

import { useState, useEffect, useMemo } from 'react';
import axios from '../axiosConfig';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { ThemedButton } from './themed';
import { getGlobalThreshold } from '../utils/preferences';
import { ChevronDown, ChevronRight, LucideSave, X } from 'lucide-react';
 // Use moment-timezone for time zone handling
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/dateTimeFormatter';
import { useDateTimePreferences } from '../hooks/useDateTimePreferences';
import ConfirmDialogShell from './dialogs/ConfirmDialogShell';

const ScheduleApplyModal = ({
  schedule,
  currentEntity,
  onClose,
  onApply,
  onUnApply,
  currentUserId,
}) => {
  const { t } = useTranslation();
  const { background, text, border, cn } = useTheme();
  useDateTimePreferences();
  const [expandedEntities, setExpandedEntities] = useState({});
  const [routes, setRoutes] = useState({});
  const [filteredRoutes, setFilteredRoutes] = useState({});
  const [selectedRoutes, setSelectedRoutes] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showOtherEntities, setShowOtherEntities] = useState(false);
  // global threshold for showing search bar for routes
  const globalThreshold = getGlobalThreshold();

  // Helper function to format included days
  const formatIncludedDays = (includedDays) => {
    if (!includedDays) return 'N/A';
    
    // Assuming 'included_days' is a comma-separated string like "0,1,2,3,4,5,6"
    const daysMap = {
      0: 'Sun',
      1: 'Mon',
      2: 'Tue',
      3: 'Wed',
      4: 'Thu',
      5: 'Fri',
      6: 'Sat'
    };
    
    const daysArray = includedDays.split(',').map(day => day.trim()).filter(day => day !== '');
    
    return daysArray.map(day => `${day}-${daysMap[day]}`).join('; ');
  };

  // Create tooltip content
  const createTippyTooltip = (schedule) => (
    <div className="p-2 max-w-sm">
      <p><strong>{schedule.created_by === currentUserId ? 'M' : 'O'}</strong> : <strong>M:</strong> Created by Me;  <strong>O:</strong> by Other User</p>
      <p><strong>F:</strong> Fixed;  <strong>OC:</strong> On-Call</p>
      <p><strong>Date:</strong> From {formatDate(schedule.start_date)} to {formatDate(schedule.end_date)}</p>
      <p><strong>Days:</strong> {formatIncludedDays(schedule.included_days)}</p>
      <p><strong>S:</strong> Single trip, <strong>R:</strong> Recurring trip</p>
      {schedule.excluded_periods?.length > 0 && (
        <p><strong>Excl P:</strong> Excluded Periods</p>
      )}
      {schedule.priority != null && schedule.priority !== "" && (
        <p>
          <strong>Priority:</strong> {`${schedule.priority} : 0-Highest,1-Second Highest, ...`}
        </p>
      )}
    </div>
  );

  // Format schedule details for display
  const getScheduleSnapshot = (schedule) => {
    if (!schedule) return '';
  
    // Snapshot Format: {Created_by, Type, Date Range, Included Days, Trip Type, Excluded Periods, Priority}
    // 0. determine who created the schedule
    const createdBy = schedule.created_by === currentUserId ? 'M' : 'O'; // M for Me, O for Others

    // 1. Determine Schedule Type
    const type = schedule.type === 'on_call' ? 'OC' : 'F'; // OC for On-Call, F for Fixed

    // 2. Format Date Range
    const startDate = formatDate(schedule.start_date);
    const endDate = formatDate(schedule.end_date);
    const dateRange = `${startDate}→${endDate}`;

    // 3. Get Included Days ID
    const includedDays = schedule.included_days
      ? schedule.included_days.split(',').map(day => parseInt(day.trim(), 10)).filter(day => !isNaN(day))
      : [];

    let includedDaysId = 'N/A';
    if (includedDays.length > 0) {
      const sortedDays = [...new Set(includedDays)].sort((a, b) => a - b);
      const ranges = [];
      let start = sortedDays[0];
      let end = sortedDays[0];

      for (let i = 1; i < sortedDays.length; i++) {
        if (sortedDays[i] === end + 1) {
          end = sortedDays[i];
        } else {
          if (start === end) {
            ranges.push(`${start}`);
          } else if (start + 1 === end) {
            ranges.push(`${start}${end}`);
          } else {
            ranges.push(`${start}→${end}`);
          }
          start = sortedDays[i];
          end = sortedDays[i];
        }
      }

      // Add the last range
      if (start === end) {
        ranges.push(`${start}`);
      } else if (start + 1 === end) {
        ranges.push(`${start}${end}`);
      } else {
        ranges.push(`${start}→${end}`);
      }

      includedDaysId = ranges.join('');
    }

    // 4. Determine Trip Type (Single, Recurring, Both)
    const tripTypes = new Set();
    if (Array.isArray(schedule.time_slots) && schedule.time_slots.length > 0) {
      schedule.time_slots.forEach(slot => {
        if (slot.type === 'single') {
          tripTypes.add('s');
        } else if (slot.type === 'recurring') {
          tripTypes.add('r');
        }
      });
    }
    const tripType = tripTypes.size === 2 ? 's/r' : tripTypes.size === 1 ? [...tripTypes][0] : '';

    // 5. Check for Excluded Periods
    const hasExcludedPeriods = schedule.excluded_periods && schedule.excluded_periods.length > 0;
    const exclP = hasExcludedPeriods ? 'excl p' : '';

    // 6. Combine All Components
    const snapshotComponents = [createdBy, type, dateRange, includedDaysId, tripType, exclP, schedule.priority]
      .filter(component => component !== undefined && component !== null && component !== '')
      .join(', ');

    return `{${snapshotComponents}}`;
  };

  const formatLocation = (location) => {
    if (!location) return '';
    const parts = location.split(',');
    return parts[0] + parts[1] + (parts.length > 2 ? ' ...' : '');
  };

  const toggleEntity = (entityName) => {
    setExpandedEntities(prev => ({
      ...prev,
      [entityName]: !prev[entityName]
    }));
  };

  const toggleRoute = (routeId) => {
    setSelectedRoutes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(routeId)) {
        newSet.delete(routeId);
      } else {
        newSet.add(routeId);
      }
      return newSet;
    });
  };

  const toggleEntityRoutes = (entityRoutes) => {
    const allSelected = entityRoutes.every(route => selectedRoutes.has(route.id));
    setSelectedRoutes(prev => {
      const newSet = new Set(prev);
      entityRoutes.forEach(route => {
        if (allSelected) {
          newSet.delete(route.id);
        } else {
          newSet.add(route.id);
        }
      });
      return newSet;
    });
  };

  const hasChanges = useMemo(() => {
    return Object.values(routes).flat().some(route =>
      (route.is_applied && !selectedRoutes.has(route.id)) ||
      (!route.is_applied && selectedRoutes.has(route.id))
    );
  }, [routes, selectedRoutes]);

  const changedRoutes = useMemo(() => (
    Object.values(routes).flat().filter(route => {
      const wasApplied = Boolean(route.is_applied);
      const willBeApplied = Boolean(selectedRoutes.has(route.id));
      return wasApplied !== willBeApplied;
    })
  ), [routes, selectedRoutes]);
  
  // fetch route status for the schedule to be applied to
  const fetchRouteStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/schedules/${schedule.id}/routes-status`);
      const routesData = response.data;
      console.log('ScheduleApplyModal: routesData', routesData);
      
      const routesByEntity = routesData.reduce((acc, route) => {
        if (!acc[route.entity_name]) {
          acc[route.entity_name] = [];
        }
        acc[route.entity_name].push(route);
        return acc;
      }, {});

      setRoutes(routesByEntity);
      setFilteredRoutes(routesByEntity);
      
      const appliedRoutes = new Set(
        routesData.filter(route => route.is_applied).map(route => route.id)
      );
      setSelectedRoutes(appliedRoutes);
    } catch (error) {
      console.error('Error fetching routes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRouteStatus();
  }, [schedule.id]);

  // filter routes based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
        setFilteredRoutes(routes);
        return;
    }

    const searchTermLower = searchTerm.toLowerCase();
    const filtered = {};

    Object.entries(routes).forEach(([entityName, entityRoutes]) => {
        const matchingRoutes = entityRoutes.filter(route => {
            const startLocation = route.start_location;
            const endLocation = route.end_location;
            
            return route.route_name.toLowerCase().includes(searchTermLower) ||
                startLocation?.toLowerCase().includes(searchTermLower) ||
                endLocation?.toLowerCase().includes(searchTermLower);
        });

        if (matchingRoutes.length > 0) {
            filtered[entityName] = matchingRoutes;
        }
    });
    setFilteredRoutes(filtered);
  }, [searchTerm, routes]);

  const handleApplyChanges = async () => {
    try {
      setLoading(true);

      const routesToApply = Object.values(routes).flat()
        .filter(route => !route.is_applied && selectedRoutes.has(route.id));

      const routesToUnapply = Object.values(routes).flat()
        .filter(route => route.is_applied && !selectedRoutes.has(route.id));

      // Apply new routes
      for (const route of routesToApply) {
        await onApply(route.id, schedule.id, true, 0);
      }

      // Unapply removed routes
      for (const route of routesToUnapply) {
        await onUnApply(schedule.id, route.id);
      }

      if (routesToApply.length > 0 && routesToUnapply.length > 0) {
        toast.success(t('Schedule applied successfully'));
        toast.success(t('Schedule unapplied successfully'));
      } else if (routesToUnapply.length > 0) {
        toast.success(t('Schedule unapplied successfully'));
      } else if (routesToApply.length > 0) {
        toast.success(t('Schedule applied successfully'));
      }

      // Check if schedule is still applied to any routes after changes
      const hasAppliedRoutes = selectedRoutes.size > 0;

      await fetchRouteStatus();
      setShowConfirmation(false);
      // After operations are complete, close the modal and Pass the applied status back to parent
      if (onClose) {
        onClose(hasAppliedRoutes);
      }
    } catch (error) {
      console.error('Error applying changes:', error);
      toast.error(t('Failed to apply changes'));
    } finally {
      setLoading(false);
    }
  };

  const renderEntityRoutes = (entityName, entityRoutes, isCurrentEntity = false) => {
    const allSelected = entityRoutes.every(route => selectedRoutes.has(route.id));
    const someSelected = entityRoutes.some(route => selectedRoutes.has(route.id));

    return (
      <div key={entityName} className={cn(
        "mb-4 rounded-lg border",
        isCurrentEntity
          ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 p-3"
          : cn("border-gray-200 dark:border-gray-700 p-3", background.primary)
      )}>
        <div
          className={cn(
            "flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors",
            "hover:bg-gray-100 dark:hover:bg-gray-700"
          )}
          onClick={() => toggleEntity(entityName)}
        >
          {expandedEntities[entityName] ? (
            <ChevronDown className={cn("w-4 h-4", text.secondary)} />
          ) : (
            <ChevronRight className={cn("w-4 h-4", text.secondary)} />
          )}
          
          <input
            type="checkbox"
            checked={allSelected}
            ref={el => {
              if (el) {
                el.indeterminate = !allSelected && someSelected;
              }
            }}
            onChange={() => toggleEntityRoutes(entityRoutes)}
            onClick={e => e.stopPropagation()}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          
          <span className={cn("font-medium", text.primary)}>{entityName}</span>
          <span className={cn("text-sm", text.muted)}>({entityRoutes.length})</span>
        </div>

        {expandedEntities[entityName] && (
          <div className="ml-6 mt-3 space-y-2">
            {entityRoutes.map(route => {
              const startLocation = route.start_location;
              const endLocation = route.end_location;
              
              return (
                <div key={route.id} className={cn(
                  "flex items-center gap-3 p-3 rounded-lg transition-colors",
                  "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                )}>
                  <input
                    type="checkbox"
                    checked={selectedRoutes.has(route.id)}
                    onChange={() => toggleRoute(route.id)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <span className={cn("font-medium", text.primary)}>{route.route_name}</span>
                    <span className={cn("text-sm ml-2", text.muted)}>
                      ({formatLocation(startLocation)} → {formatLocation(endLocation)})
                    </span>
                    {Boolean(route.is_applied) && (
                      <span className="text-green-600 dark:text-green-400 text-sm ml-2 font-medium">
                        {t('Applied')}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // when clicking "Cancel" button, pass back the original schedule's applied status
  const handleClose = () => {
    // Use the existing schedule.isScheduleApplied value
    onClose(schedule.isScheduleApplied);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className={cn(
          "w-full max-w-2xl mx-4 rounded-lg shadow-xl flex flex-col max-h-[90vh]",
          background.primary,
          border.default,
          "border"
        )}>
          {/* Header */}
          <div className={cn(
            "flex items-center justify-between p-6",
            border.default,
            "border-b"
          )}>
            <div className="flex-1 space-y-2">
              <h2 className={cn("text-xl font-bold", text.primary)}>
                {schedule?.name || t('Unnamed Schedule')}
              </h2>
                <Tippy
                  content={createTippyTooltip(schedule)}
                  placement="bottom"
                  interactive={true}
                >
                  <div className={cn("text-xs font-mono cursor-help", text.muted)}>
                    {getScheduleSnapshot(schedule)}
                  </div>
                </Tippy>
              </div>
              
              <div className="flex items-center gap-4">
                {Object.values(routes).flat().length >= globalThreshold && (
                  <input
                    type="text"
                    className={cn(
                      "px-3 py-2 rounded-lg text-sm transition-colors w-64",
                      "border-2 border-gray-300 dark:border-gray-600",
                      "focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800",
                      "bg-white dark:bg-gray-700",
                      text.primary,
                      "placeholder-gray-500 dark:placeholder-gray-400"
                    )}
                    placeholder={t("Search routes...")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                )}
                <button
                  onClick={handleClose}
                  className={cn(
                    "p-2 rounded-lg transition-colors",
                    "hover:bg-gray-100 dark:hover:bg-gray-700",
                    text.secondary
                  )}
                >
                  <X size={20} />
                </button>
              </div>
          </div>

          {/* Content */}
          <div className="flex-grow overflow-y-auto p-6">
            {loading ? (
              <div className="flex justify-center items-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto space-y-4">
                {/* Current Entity Section */}
                {filteredRoutes[currentEntity.entityName] && renderEntityRoutes(currentEntity.entityName, filteredRoutes[currentEntity.entityName], true)}

                {/* Toggle for other entities */}
                {Object.keys(routes).length > 1 && (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={showOtherEntities}
                      onChange={(e) => setShowOtherEntities(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                      {t("Show other entities")}
                    </span>
                  </div>
                )}

                {/* Other Entities Section */}
                {showOtherEntities && Object.entries(filteredRoutes)
                  .filter(([entityName]) => entityName !== currentEntity.entityName)
                  .map(([entityName, entityRoutes]) => renderEntityRoutes(entityName, entityRoutes))
                }

                {Object.keys(filteredRoutes).length === 0 && (
                  <div className="text-center text-gray-500 py-4">
                    {searchTerm ? t('No routes found matching your search') : t('No routes available')}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={cn(
            "flex justify-end items-center gap-3 p-6",
            border.default,
            "border-t"
          )}>
            <ThemedButton
              onClick={() => setShowConfirmation(true)}
              disabled={!hasChanges}
              variant={hasChanges ? "primary" : "secondary"}
            >              
              {t('Apply Changes')}
            </ThemedButton>
            <ThemedButton
              onClick={handleClose}
              variant="secondary"
            >              
              {t('Cancel')}
            </ThemedButton>
          </div>
        </div>
      </div>

      <ConfirmDialogShell
        isOpen={showConfirmation}
        onClose={() => !loading && setShowConfirmation(false)}
        onConfirm={handleApplyChanges}
        title={t('Confirm Changes')}
        confirmLabel={t('Confirm')}
        confirmIcon={LucideSave}
        isLoading={loading}
        isDisabled={loading}
        closeOnBackdropClick={!loading}
      >
        <div className="space-y-4">
          <div className="text-sm">
            {changedRoutes.map(route => (
              <div key={route.id} className="mb-1">
                <span className={selectedRoutes.has(route.id) ? 'text-green-600' : 'text-red-600'}>
                  {selectedRoutes.has(route.id) ? '+ ' : '- '}
                </span>
                {route.route_name} ({route.entity_name})
              </div>
            ))}
          </div>

          <div className={cn("text-sm", text.secondary)}>
            {t('Total changes')}: {changedRoutes.length}
          </div>
        </div>
      </ConfirmDialogShell>
    </>
  );
};

export default ScheduleApplyModal;
